/**
 * useFieldNotesSave — THE ONLY save path for Site Visit field notes.
 *
 * Replaces the previously divergent handlers in FieldNotesPage and
 * SiteVisitDetailPage. Both manual save and the 20-second autosave loop go
 * through this hook so behavior, toasts, and the save-status badge stay
 * consistent across the whole app.
 *
 * State machine (exposed via `status`):
 *   - 'clean'         no unsaved changes; server matches local
 *   - 'saving'        network request in flight (auto or manual)
 *   - 'saved'         server confirmed; cleared local draft (manual) or kept (auto)
 *   - 'saved_locally' server attempt FAILED; data is queued for retry
 *   - 'pending_retry' queue is non-empty and processor is between attempts
 *   - 'sync_failed'   queue has a row that won't auto-retry (auth/4xx)
 *
 * Contract:
 *   - We NEVER pre-check navigator.onLine to decide whether to attempt the
 *     network. Browser flags lie. Always try the request; classify by outcome.
 *   - The word "offline" only appears in user-facing copy when
 *     navigator.onLine === false at fail-time (post-request).
 *   - Photos are isolated. We never send base64 photo blobs in this payload.
 *     URL-less photos are filtered out so they can't pollute the server doc.
 *     A separate PhotoUploadManager queue syncs them; failures there do NOT
 *     affect the notes save status.
 */
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import {
  saveFieldNotesDraft,
  clearFieldNotesDraft,
} from '@/lib/offlineSync';
import { queueOfflineAction, OFFLINE_ACTIONS, getAllActions } from '@/lib/offlineQueue';
import { remove, STORES } from '@/lib/offlineDB';

export const SAVE_STATUS = Object.freeze({
  CLEAN: 'clean',
  SAVING: 'saving',
  SAVED: 'saved',
  SAVED_LOCALLY: 'saved_locally',
  PENDING_RETRY: 'pending_retry',
  SYNC_FAILED: 'sync_failed',
});

// Snake_case payload the backend expects. Centralized here so manual & autosave
// can't diverge again.
const toApiPayload = (notes) => ({
  internal_notes: notes.internalNotes || '',
  project_notes: notes.clientNotes || '',
  client_expectations: notes.clientExpectations || null,
  structured_measurements: notes.measurements || [],
  // Drop URL-less photos so they can't be saved as broken references.
  // PhotoUploadManager syncs them on its own clock; the next save picks up
  // the URLs once they exist.
  photos: (notes.photos || [])
    .filter(p => p.url || p.server_id)
    .map(p => ({
      id: p.id,
      url: p.url,
      server_id: p.server_id,
      name: p.name,
      tag: p.tag,
      timestamp: p.timestamp,
    })),
  sketches: notes.sketches || [],
  site_conditions: notes.siteConditions || [],
  custom_conditions: notes.customConditions || [],
  checklist: notes.checklist || [],
  is_checklist_complete: !!notes.isChecklistComplete,
  internal_voice_recordings: notes.internalVoiceRecordings || [],
  client_voice_recordings: notes.clientVoiceRecordings || [],
});

const classifyError = (err) => {
  const status = err?.response?.status;
  const reallyOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (status === 401 || status === 403) return { kind: 'auth', status, reallyOffline: false };
  if (status && status >= 400 && status < 500) return { kind: 'validation', status, reallyOffline: false };
  if (reallyOffline) return { kind: 'offline', status: null, reallyOffline: true };
  return { kind: 'network', status: status || null, reallyOffline: false };
};

export function useFieldNotesSave({ organization, user, visitId }) {
  const [status, setStatus] = useState(SAVE_STATUS.CLEAN);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError, setLastError] = useState(null);
  // In-flight guard — prevents an autosave from racing a manual save.
  const inflightRef = useRef(false);

  /**
   * Internal: enqueue the change for background retry. Always best-effort.
   * Returns true on success.
   */
  const enqueue = useCallback(async (notes) => {
    if (!organization?.id || !visitId) return false;
    try {
      await queueOfflineAction(
        OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES,
        {
          site_visit_id: visitId,
          field_notes: toApiPayload(notes),
          user_id: user?.id,
          user_name: user?.name,
        },
        organization.id,
      );
      return true;
    } catch (e) {
      console.error('[useFieldNotesSave] enqueue failed:', e);
      return false;
    }
  }, [organization?.id, user?.id, user?.name, visitId]);

  /**
   * Core save. mode='manual' surfaces toasts; mode='auto' is silent
   * (no toasts), but updates the status badge.
   */
  const save = useCallback(async (notes, { mode = 'manual' } = {}) => {
    if (!organization?.id || !visitId) {
      return { ok: false, status: SAVE_STATUS.SYNC_FAILED };
    }
    // Coalesce: if a save is already running, drop this one. The latest
    // state will get picked up by the next tick or by the user's next save.
    if (inflightRef.current) {
      return { ok: false, status, deferred: true };
    }
    inflightRef.current = true;
    setStatus(SAVE_STATUS.SAVING);

    // ALWAYS persist a local draft first. This is the indestructible safety
    // net — even if the network attempt below burns down, the data is on
    // disk and the sync queue + on-load recovery handle the rest.
    try {
      await saveFieldNotesDraft(visitId, notes, organization.id);
    } catch (e) {
      console.warn('[useFieldNotesSave] local draft save failed (continuing):', e);
    }

    try {
      await apiHelpers.updateSiteVisit(
        organization.id,
        visitId,
        toApiPayload(notes),
        user?.id,
        user?.name,
      );
      // Server confirmed. CRITICAL: drop any stale UPDATE_SITE_VISIT_FIELD_NOTES
      // queue items for THIS visit. Otherwise a queued snapshot from 30s ago
      // can later overwrite the fresh server data when the queue finally
      // drains — the "regression race" that ate SolidHome's notes
      // (Feb 2026 post-mortem, Bug C).
      try {
        const all = await getAllActions();
        for (const a of all) {
          if (a.action_type === OFFLINE_ACTIONS.UPDATE_SITE_VISIT_FIELD_NOTES
              && a.payload?.site_visit_id === visitId) {
            await remove(STORES.OFFLINE_QUEUE, a.id);
          }
        }
      } catch (e) {
        console.warn('[useFieldNotesSave] could not prune stale queue items:', e);
      }
      // On manual save we clear the draft because the user has explicitly
      // committed. On autosave we keep the draft as a redundant local copy
      // until the user explicitly saves.
      if (mode === 'manual') {
        try { await clearFieldNotesDraft(visitId); } catch { /* ignore */ }
      }
      setStatus(SAVE_STATUS.SAVED);
      setLastSavedAt(new Date());
      setLastError(null);
      if (mode === 'manual') {
        toast.success('Saved to cloud');
      }
      return { ok: true, status: SAVE_STATUS.SAVED };
    } catch (err) {
      const classified = classifyError(err);
      const queued = await enqueue(notes);
      const next = queued
        ? (classified.kind === 'auth' || classified.kind === 'validation'
            ? SAVE_STATUS.SYNC_FAILED  // won't auto-retry — needs human action
            : SAVE_STATUS.SAVED_LOCALLY)
        : SAVE_STATUS.SYNC_FAILED;
      setStatus(next);
      setLastError({
        kind: classified.kind,
        status: classified.status,
        message: err?.message || 'Save failed',
        at: new Date(),
      });

      if (mode === 'manual') {
        if (!queued) {
          toast.error('Save failed — try again', {
            description: 'We could not reach the server and could not queue the change locally.',
          });
        } else if (classified.kind === 'auth') {
          toast.warning('Session expired — queued for retry', {
            description: 'Sign in again and it will sync automatically.',
          });
        } else if (classified.kind === 'validation') {
          toast.warning('Server rejected the save', {
            description: 'Queued for retry. Check details in Developer Mode.',
          });
        } else if (classified.kind === 'offline') {
          toast.success('Saved locally', {
            description: 'You are offline. Will sync automatically when you reconnect.',
          });
        } else {
          toast.warning('Saved locally — server unreachable', {
            description: 'Your change is queued and will sync shortly.',
          });
        }
      }
      return { ok: false, status: next, classified };
    } finally {
      inflightRef.current = false;
    }
  }, [organization?.id, user?.id, user?.name, visitId, enqueue, status]);

  /**
   * Retry-now button handler. Tries the most recent in-memory notes again.
   * Caller passes the current notes object.
   */
  const retry = useCallback(async (notes) => {
    return save(notes, { mode: 'manual' });
  }, [save]);

  const markClean = useCallback(() => setStatus(SAVE_STATUS.CLEAN), []);

  return {
    status,
    lastSavedAt,
    lastError,
    save,                  // (notes, {mode}) => result
    autoSave: useCallback((notes) => save(notes, { mode: 'auto' }), [save]),
    retry,
    markClean,
    SAVE_STATUS,
  };
}

export default useFieldNotesSave;
